import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function escapeHtml(value) {
  return value === null || value === undefined ? "" : String(value);
}

function formatValue(value, fallback = "") {
  return value === null || value === undefined || value === ""
    ? fallback
    : String(value);
}

function getRouteId(row) {
  return row.sentinel_route_id || row.route_id || row.id || row.SlNo;
}

function getRouteName(row) {
  return row.r_name || row.route_name || row.name || "";
}

function getFrom(row) {
  return row.r_from || row.from_name || row.from_location || "";
}

function getTo(row) {
  return row.r_to || row.to_name || row.to_location || "";
}

function getCheckpoints(row) {
  if (!row.checkpoints) return [];
  return String(row.checkpoints).split("|||").filter(Boolean);
}

export default function SentinelRouteList() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const initialPage = Number(params.get("page") || 1);
  const initialSearch = params.get("search") || "";

  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [searchInput, setSearchInput] = useState(initialSearch);
  const [search, setSearch] = useState(initialSearch);
  const [page, setPage] = useState(initialPage > 0 ? initialPage : 1);

  const [routes, setRoutes] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);

  const [allocModalOpen, setAllocModalOpen] = useState(false);
  const [allocSaving, setAllocSaving] = useState(false);
  const [allocGuards, setAllocGuards] = useState([]);

  const [allocRoute, setAllocRoute] = useState({
    route_id: "",
    route_name: "",
  });

  const [allocForm, setAllocForm] = useState({
    guard_member_id: "",
    shift_label: "Morning",
    shift_label_custom: "",
    start_time: "",
    end_time: "",
  });

  const recordsPerPage = 10;

  useEffect(() => {
    if (!successMessage && !errorMessage) return;

    const timer = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token, page, search);
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    if (!token) return;

    fetchPageData(dashboardData, token, page, search);
  }, [page, search]);

  const fetchPageData = async (local, token, currentPage, currentSearch) => {
    try {
      setLoading(true);
      setErrorMessage("");

      const [dashboardRes, routesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(
          `${API_BASE_URL}/sentinel_route/list?page=${currentPage}&limit=${recordsPerPage}&search=${encodeURIComponent(
            currentSearch || ""
          )}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        ),
      ]);

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!routesRes.ok) throw new Error("Failed to fetch sentinel routes");

      const dashboardResult = await dashboardRes.json();
      const routesResult = await routesRes.json();

      const companyDetails = dashboardResult?.data?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      const listData = routesResult?.data || {};

      setRoutes(Array.isArray(listData.routes) ? listData.routes : []);
      setTotalRecords(Number(listData.totalRecords || 0));
      setTotalPages(Math.max(1, Number(listData.totalPages || 1)));
    } catch (error) {
      console.error("Sentinel route list error:", error);
      setErrorMessage(error.message || "Failed to load sentinel route list.");
      setRoutes([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const updateUrl = (nextPage, nextSearch) => {
    const query = new URLSearchParams();

    if (nextPage > 1) query.set("page", String(nextPage));
    if (nextSearch?.trim()) query.set("search", nextSearch.trim());

    navigate(`/sentinel_route-list${query.toString() ? `?${query.toString()}` : ""}`);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    const nextSearch = searchInput.trim();

    setPage(1);
    setSearch(nextSearch);
    updateUrl(1, nextSearch);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
    navigate("/sentinel_route-list");
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    updateUrl(nextPage, search);
  };

  const viewRoute = (route) => {
    setSelectedRoute(route);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setSelectedRoute(null);
    setViewModalOpen(false);
  };

  const goToRouteAllocations = (routeId, routeName) => {
    navigate(
      `/sentinel_route_allocation?route_id=${routeId}&route_name=${encodeURIComponent(
        routeName || ""
      )}`
    );
  };

  const confirmDelete = async (routeId) => {
    const ok = window.confirm(
      "Are you sure you want to delete this sentinel route?\n\n" +
        "This will permanently delete:\n" +
        "• Sentinel route details\n" +
        "• All checkpoints\n" +
        "• All geofences\n" +
        "• All route allocations\n" +
        "• All geofence assignments\n\n" +
        "This action cannot be undone."
    );

    if (!ok) return;

    try {
      setDeletingId(routeId);
      setErrorMessage("");
      setSuccessMessage("");

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/sentinel_route/${routeId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to delete sentinel route");
      }

      setSuccessMessage("Sentinel route and all associated data deleted successfully!");

      if (getRouteId(selectedRoute) === routeId) {
        closeViewModal();
      }

      await fetchPageData(dashboardData, token, page, search);
    } catch (error) {
      console.error("Delete sentinel route error:", error);
      setErrorMessage(error.message || "Failed to delete sentinel route.");
    } finally {
      setDeletingId(null);
    }
  };

  const fetchGuards = async () => {
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/sentinel_users/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        cache: "no-store",
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to fetch guards");
      }

      setAllocGuards(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      console.error("Fetch guards error:", error);
      setAllocGuards([]);
    }
  };

  const openAllocationModal = async (routeId, routeName) => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    now.setHours(now.getHours() + 1);

    const pad = (n) => String(n).padStart(2, "0");

    const defaultStart = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate()
    )}T${pad(now.getHours())}:${pad(now.getMinutes())}`;

    const end = new Date(now);
    end.setHours(end.getHours() + 8);

    const defaultEnd = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(
      end.getDate()
    )}T${pad(end.getHours())}:${pad(end.getMinutes())}`;

    setAllocRoute({
      route_id: routeId,
      route_name: routeName,
    });

    setAllocForm({
      guard_member_id: "",
      shift_label: "Morning",
      shift_label_custom: "",
      start_time: defaultStart,
      end_time: defaultEnd,
    });

    setAllocModalOpen(true);
    await fetchGuards();
  };

  const closeAllocModal = () => {
    setAllocModalOpen(false);

    setAllocRoute({
      route_id: "",
      route_name: "",
    });

    setAllocForm({
      guard_member_id: "",
      shift_label: "Morning",
      shift_label_custom: "",
      start_time: "",
      end_time: "",
    });

    setAllocGuards([]);
  };

  const handleAllocChange = (e) => {
    const { name, value } = e.target;

    setAllocForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveAllocation = async () => {
    if (
      !allocRoute.route_id ||
      !allocForm.guard_member_id ||
      !allocForm.shift_label ||
      !allocForm.start_time ||
      !allocForm.end_time
    ) {
      window.alert("Please fill all required fields.");
      return;
    }

    if (allocForm.shift_label === "Custom" && !allocForm.shift_label_custom.trim()) {
      window.alert("Please enter custom shift label.");
      return;
    }

    try {
      setAllocSaving(true);

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const payload = {
        route_id: parseInt(allocRoute.route_id, 10),
        guard_member_id: allocForm.guard_member_id,
        shift_label: allocForm.shift_label,
        shift_label_custom: allocForm.shift_label_custom,
        start_time: allocForm.start_time,
        end_time: allocForm.end_time,
      };

      const res = await fetch(`${API_BASE_URL}/sentinel_allocate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save allocation");
      }

      setSuccessMessage(data.message || "Sentinel allocation saved successfully.");
      closeAllocModal();
    } catch (error) {
      console.error("Save sentinel allocation error:", error);
      window.alert(error.message || "Unexpected error saving allocation.");
    } finally {
      setAllocSaving(false);
    }
  };

  const paginationItems = useMemo(() => {
    const items = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);

    if (start > 1) {
      items.push(1);
      if (start > 2) items.push("...");
    }

    for (let i = start; i <= end; i += 1) {
      items.push(i);
    }

    if (end < totalPages) {
      if (end < totalPages - 1) items.push("...");
      items.push(totalPages);
    }

    return items;
  }, [page, totalPages]);

  const routePage = "/sentinel_add_Route";

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-3" />
        <p className="text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading sentinel route list...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="sentinel-routes"
      activeMenu="sentinel"
    >
      <PageHeader onAddRoute={() => navigate("/sentinel_add_Route")} 
     onEscortList={()=> navigate("/sentinel_escort_list")}/>

      {successMessage ? <Alert type="success" message={successMessage} /> : null}
      {errorMessage ? <Alert type="error" message={errorMessage} /> : null}

      <FormSection
        title="Sentinel Route Checkpoints List"
        subtitle="View sentinel routes, checkpoints, allocations, and manage route actions"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <StatusCard label="Total Sentinel Routes" value={totalRecords} />
          <StatusCard label="Page Status" value={`${page} / ${totalPages}`} />
        </div>

        <form onSubmit={handleSearchSubmit} className="flex gap-3 flex-wrap">
          <Input
            type="text"
            placeholder="Search by route name, from, or to..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="flex-1 min-w-[260px]"
          />

          <button
            type="submit"
            className="px-5 py-3 rounded-xl text-white text-sm font-semibold bg-gradient-to-br from-slate-800 to-blue-600 shadow-lg shadow-blue-500/20"
            style={{ fontFamily: FONT_UI }}
          >
            Search
          </button>

          {search ? (
            <button
              type="button"
              onClick={clearSearch}
              className="px-5 py-3 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
              style={{ fontFamily: FONT_UI }}
            >
              Clear
            </button>
          ) : null}
        </form>

        <div className="mt-6">
          {routes.length > 0 ? (
            <>
              <div className="w-full overflow-x-auto border border-slate-200 rounded-2xl">
                <table className="w-full border-collapse bg-white min-w-[1100px]">
                  <thead>
                    <tr className="bg-slate-50">
                      <Th>S. No</Th>
                      <Th>Route Name</Th>
                      <Th>From</Th>
                      <Th>To</Th>
                      <Th>Checkpoints</Th>
                      <Th>Allocations</Th>
                      <Th>Actions</Th>
                    </tr>
                  </thead>

                  <tbody>
                    {routes.map((row, index) => {
                      const routeId = getRouteId(row);
                      const routeName = getRouteName(row);
                      const checkpoints = getCheckpoints(row);

                      return (
                        <tr key={routeId} className="hover:bg-slate-50">
                          <Td>
                            <strong>{(page - 1) * recordsPerPage + index + 1}</strong>
                          </Td>

                          <Td>
                            <strong>{formatValue(routeName)}</strong>
                          </Td>

                          <Td>{formatValue(getFrom(row), "Not specified")}</Td>
                          <Td>{formatValue(getTo(row), "Not specified")}</Td>

                          <Td>
                            {checkpoints.length > 0 ? (
                              <>
                                <div className="flex gap-2 flex-wrap">
                                  {checkpoints.slice(0, 3).map((checkpoint, i) => (
                                    <span
                                      key={`${routeId}-${i}`}
                                      className="inline-flex items-center px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 font-semibold text-xs"
                                      style={{ fontFamily: FONT_UI }}
                                    >
                                      {escapeHtml(checkpoint)}
                                    </span>
                                  ))}

                                  {checkpoints.length > 3 ? (
                                    <span
                                      className="inline-flex items-center px-3 py-1.5 rounded-full bg-slate-200 text-slate-600 font-semibold text-xs"
                                      style={{ fontFamily: FONT_UI }}
                                    >
                                      +{checkpoints.length - 3} more
                                    </span>
                                  ) : null}
                                </div>

                                <div
                                  className="mt-2 text-xs text-slate-500"
                                  style={{ fontFamily: FONT_UI }}
                                >
                                  Total:{" "}
                                  {Number(row.checkpoint_count || checkpoints.length || 0)}{" "}
                                  checkpoints
                                </div>
                              </>
                            ) : (
                              <span
                                className="text-slate-400 italic"
                                style={{ fontFamily: FONT_UI }}
                              >
                                No checkpoints
                              </span>
                            )}
                          </Td>

                          <Td>
                            <div className="flex gap-2 flex-wrap">
                              <button
                                type="button"
                                onClick={() => openAllocationModal(routeId, routeName)}
                                className="px-4 py-2 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600"
                                style={{ fontFamily: FONT_UI }}
                              >
                                Allocate
                              </button>

                              <button
                                type="button"
                                onClick={() => goToRouteAllocations(routeId, routeName)}
                                className="px-4 py-2 rounded-xl bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600"
                                style={{ fontFamily: FONT_UI }}
                              >
                                View
                              </button>
                            </div>
                          </Td>

                          <Td>
                            <div className="flex gap-2 flex-wrap">
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(`/sentinel_add_Route?edit_id=${routeId}`)
                                }
                                className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
                                style={{ fontFamily: FONT_UI }}
                              >
                                Edit
                              </button>

                              <button
                                type="button"
                                onClick={() => viewRoute(row)}
                                className="px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700"
                                style={{ fontFamily: FONT_UI }}
                              >
                                View
                              </button>

                              <button
                                type="button"
                                onClick={() => confirmDelete(routeId)}
                                className="px-4 py-2 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed"
                                style={{ fontFamily: FONT_UI }}
                                disabled={deletingId === routeId}
                              >
                                {deletingId === routeId ? "Deleting..." : "Delete"}
                              </button>
                            </div>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 ? (
                <div className="flex items-center justify-center gap-3 flex-wrap mt-5">
                  {page > 1 ? (
                    <PageButton onClick={() => handlePageChange(page - 1)}>
                      Previous
                    </PageButton>
                  ) : null}

                  {paginationItems.map((item, index) =>
                    item === "..." ? (
                      <span
                        key={`dots-${index}`}
                        className="text-slate-500 text-lg"
                        style={{ fontFamily: FONT_UI }}
                      >
                        ...
                      </span>
                    ) : (
                      <PageButton
                        key={item}
                        active={item === page}
                        onClick={() => handlePageChange(item)}
                      >
                        {item}
                      </PageButton>
                    )
                  )}

                  {page < totalPages ? (
                    <PageButton onClick={() => handlePageChange(page + 1)}>
                      Next
                    </PageButton>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : (
            <EmptyState
              title="No Sentinel Routes Found"
              subtitle={
                search
                  ? `No sentinel routes match your search: "${search}"`
                  : "No sentinel routes have been created yet."
              }
            >
              {search ? (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold bg-gradient-to-br from-slate-800 to-blue-600 shadow-lg shadow-blue-500/20"
                  style={{ fontFamily: FONT_UI }}
                >
                  Show All Routes
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate("/sentinel_add_Route")}
                  className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold bg-gradient-to-br from-slate-800 to-blue-600 shadow-lg shadow-blue-500/20"
                  style={{ fontFamily: FONT_UI }}
                >
                  Create First Sentinel Route
                </button>
              )}
            </EmptyState>
          )}
        </div>
      </FormSection>

      {viewModalOpen && selectedRoute ? (
        <Modal onClose={closeViewModal}>
          <ModalHeader title="Sentinel Route Details" onClose={closeViewModal} />

          <div className="p-6 bg-slate-50">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <DetailItem label="Route ID" value={getRouteId(selectedRoute)} />

              <DetailItem
                label="Route Name"
                value={<strong>{formatValue(getRouteName(selectedRoute))}</strong>}
              />

              <DetailItem
                label="From Location"
                value={formatValue(getFrom(selectedRoute), "Not specified")}
              />

              <DetailItem
                label="To Location"
                value={formatValue(getTo(selectedRoute), "Not specified")}
              />

              <DetailItem
                label="Total Checkpoints"
                value={`${Number(
                  selectedRoute.checkpoint_count || getCheckpoints(selectedRoute).length || 0
                )} checkpoints`}
              />

              <DetailItem
                label="Created Date"
                value={formatValue(
                  selectedRoute.created_date || selectedRoute.created_at,
                  "Not specified"
                )}
              />

              <DetailItem
                label="Status"
                value={
                  Number(selectedRoute.is_active ?? selectedRoute.active ?? 1) === 1
                    ? "Active"
                    : "Inactive"
                }
              />
            </div>

            <div className="mt-6">
              <h3
                className="m-0 mb-3 text-lg font-bold text-slate-900"
                style={{ fontFamily: FONT_UI }}
              >
                Checkpoints
              </h3>

              {getCheckpoints(selectedRoute).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {getCheckpoints(selectedRoute).map((checkpoint, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-2xl border border-slate-200 p-4"
                    >
                      <div
                        className="inline-flex items-center px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 font-bold text-xs"
                        style={{ fontFamily: FONT_UI }}
                      >
                        Checkpoint {index + 1}
                      </div>

                      <div className="mt-2 text-slate-900" style={{ fontFamily: FONT_UI }}>
                        {checkpoint}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  className="border border-dashed border-slate-300 rounded-2xl bg-white p-6 text-center text-slate-500"
                  style={{ fontFamily: FONT_UI }}
                >
                  No checkpoints defined for this sentinel route
                </div>
              )}
            </div>
          </div>

          <div className="px-6 py-4 bg-white border-t border-slate-200 flex justify-end gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => navigate(`/sentinel_add_Route?edit_id=${getRouteId(selectedRoute)}`)}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold bg-gradient-to-br from-slate-800 to-blue-600 shadow-lg shadow-blue-500/20"
              style={{ fontFamily: FONT_UI }}
            >
              Edit Route
            </button>

            <button
              type="button"
              onClick={() => confirmDelete(getRouteId(selectedRoute))}
              className="px-5 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600"
              style={{ fontFamily: FONT_UI }}
            >
              Delete Route
            </button>
          </div>
        </Modal>
      ) : null}

      {allocModalOpen ? (
        <Modal onClose={closeAllocModal}>
          <ModalHeader
            title="Allocate Guard to Sentinel Route"
            onClose={closeAllocModal}
          />

          <div className="p-6 bg-slate-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Route">
                <Input type="text" value={allocRoute.route_name} disabled />
              </Field>

              <Field label="Shift">
                <Select
                  name="shift_label"
                  value={allocForm.shift_label}
                  onChange={handleAllocChange}
                >
                  <option value="Morning">Morning</option>
                  <option value="Evening">Evening</option>
                  <option value="Night">Night</option>
                  <option value="Custom">Custom</option>
                </Select>
              </Field>

              <div className="md:col-span-2">
                <Field label="Guard">
                  <Select
                    name="guard_member_id"
                    value={allocForm.guard_member_id}
                    onChange={handleAllocChange}
                    required
                  >
                    <option value="">
                      {allocGuards.length ? "-- Select Guard --" : "Loading guards..."}
                    </option>

                    {allocGuards.map((guard) => (
                      <option
                        key={guard.member_id || guard.fld_memberId}
                        value={guard.member_id || guard.fld_memberId}
                      >
                        {guard.username || guard.fld_username || guard.name} (
                        {guard.member_id || guard.fld_memberId})
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>

              <Field label="Start Date & Time">
                <Input
                  type="datetime-local"
                  name="start_time"
                  value={allocForm.start_time}
                  onChange={handleAllocChange}
                  required
                />
              </Field>

              <Field label="End Date & Time">
                <Input
                  type="datetime-local"
                  name="end_time"
                  value={allocForm.end_time}
                  onChange={handleAllocChange}
                  required
                />
              </Field>

              {allocForm.shift_label === "Custom" ? (
                <div className="md:col-span-2">
                  <Field label="Custom shift label">
                    <Input
                      type="text"
                      name="shift_label_custom"
                      value={allocForm.shift_label_custom}
                      onChange={handleAllocChange}
                      placeholder="e.g. OT Shift / Special Duty"
                    />
                  </Field>
                </div>
              ) : null}
            </div>
          </div>

          <div className="px-6 py-4 bg-white border-t border-slate-200 flex justify-end gap-3 flex-wrap">
            <button
              type="button"
              onClick={closeAllocModal}
              className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
              style={{ fontFamily: FONT_UI }}
            >
              Close
            </button>

            <button
              type="button"
              onClick={saveAllocation}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold bg-gradient-to-br from-slate-800 to-blue-600 shadow-lg shadow-blue-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ fontFamily: FONT_UI }}
              disabled={allocSaving}
            >
              {allocSaving ? "Saving..." : "Save Allocation"}
            </button>
          </div>
        </Modal>
      ) : null}
    </Layout>
  );
}

function PageHeader({ onAddRoute, onEscortList }) {
  const skin = useSkin();

  return (
    <>
      <div className="mb-6">
        <h2
          className="m-0 text-[26px] font-normal text-slate-900 tracking-tight"
          style={{ fontFamily: FONT_HEADING }}
        >
          Sentinel Route Management
        </h2>

        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          Manage sentinel route records, checkpoints, and guard allocations
        </p>
      </div>

      <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6 flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h3
            className="m-0 text-[22px] font-normal text-slate-900"
            style={{ fontFamily: FONT_HEADING }}
          >
            Sentinel Route List
          </h3>

          <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
            View all sentinel routes and manage checkpoints, route details, and allocations
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={onEscortList}
            className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
            style={{ fontFamily: FONT_UI }}
          >
            Escort List
          </button>

          <button
            type="button"
            onClick={onAddRoute}
            className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-lg shadow-blue-500/20"
            style={{
              background: skin.gradient,
              fontFamily: FONT_UI,
            }}
          >
            Add New Sentinel Route
          </button>
        </div>
      </div>
    </>
  );
}

function Alert({ type, message }) {
  const isSuccess = type === "success";

  return (
    <div
      className={`mb-5 px-4 py-3.5 rounded-xl text-sm font-medium border ${
        isSuccess
          ? "bg-green-50 border-green-200 text-green-700"
          : "bg-red-50 border-red-200 text-red-700"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {message}
    </div>
  );
}

function FormSection({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6">
      <div className="mb-5">
        <h3
          className="m-0 text-[22px] font-normal text-slate-900"
          style={{ fontFamily: FONT_HEADING }}
        >
          {title}
        </h3>

        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          {subtitle}
        </p>
      </div>

      {children}
    </div>
  );
}

function StatusCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4">
      <div className="text-sm text-slate-500 mb-1.5" style={{ fontFamily: FONT_UI }}>
        {label}
      </div>

      <div
        className="text-[22px] font-bold text-slate-900"
        style={{ fontFamily: FONT_HEADING }}
      >
        {value}
      </div>
    </div>
  );
}

function EmptyState({ title, subtitle, children }) {
  return (
    <div className="border border-dashed border-slate-300 rounded-2xl bg-slate-50 px-6 py-10 text-center">
      <div
        className="text-[22px] font-bold text-slate-900 mb-2"
        style={{ fontFamily: FONT_HEADING }}
      >
        {title}
      </div>

      <div className="text-sm text-slate-500 mb-4" style={{ fontFamily: FONT_UI }}>
        {subtitle}
      </div>

      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label
        className="block mb-2 text-sm font-semibold text-slate-900"
        style={{ fontFamily: FONT_UI }}
      >
        {label}
      </label>

      {children}
    </div>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`w-full px-3.5 py-3 rounded-xl border border-slate-200 outline-none text-sm text-slate-900 bg-white box-border focus:ring-2 focus:ring-blue-100 focus:border-blue-500 disabled:bg-slate-100 disabled:text-slate-500 ${className}`}
      style={{ fontFamily: FONT_UI }}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`w-full px-3.5 py-3 rounded-xl border border-slate-200 outline-none text-sm text-slate-900 bg-white box-border focus:ring-2 focus:ring-blue-100 focus:border-blue-500 ${className}`}
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </select>
  );
}

function Th({ children }) {
  return (
    <th
      className="text-left px-4 py-3.5 text-sm text-slate-900 bg-slate-50 border-b border-slate-200 whitespace-nowrap"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </th>
  );
}

function Td({ children }) {
  return (
    <td
      className="px-4 py-3.5 text-sm text-slate-700 border-b border-slate-100 align-top"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </td>
  );
}

function PageButton({ children, active = false, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`min-w-[46px] px-4 py-2.5 rounded-xl text-sm font-semibold ${
        active
          ? "text-white bg-gradient-to-br from-slate-800 to-blue-600 shadow-lg shadow-blue-500/20"
          : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </button>
  );
}

function Modal({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-5"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[920px] bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, onClose }) {
  return (
    <div className="px-6 py-4 bg-blue-700 text-white flex items-center justify-between">
      <h2 className="m-0 text-[22px]" style={{ fontFamily: FONT_HEADING }}>
        {title}
      </h2>

      <button
        type="button"
        onClick={onClose}
        className="border-0 bg-transparent text-white text-3xl leading-none cursor-pointer"
      >
        ×
      </button>
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div
        className="mb-1.5 text-sm text-slate-500 font-semibold"
        style={{ fontFamily: FONT_UI }}
      >
        {label}
      </div>

      <div className="text-[15px] text-slate-900" style={{ fontFamily: FONT_UI }}>
        {value}
      </div>
    </div>
  );
}